import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { azn, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import ReferralCard from "@/components/ReferralCard";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Wallet, TrendingUp, Sprout, Coins, ArrowUpRight, Store, HandCoins } from "lucide-react";

function livePending(inv) {
  if (inv.status !== "active") return 0;
  const end = new Date(inv.end_at).getTime();
  const last = new Date(inv.last_collected_at).getTime();
  const eff = Math.min(Date.now(), end);
  if (eff <= last) return 0;
  return (inv.daily_income * (eff - last)) / 86400000;
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="p-6 rounded-2xl border border-border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent ? "bg-accent/15" : "bg-secondary"}`}>
          <Icon className={`w-4 h-4 ${accent ? "text-accent" : "text-primary"}`} />
        </span>
      </div>
      <p className="font-heading text-2xl font-bold mt-3">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [collecting, setCollecting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, inv] = await Promise.all([api.get("/wallet"), api.get("/investments")]);
      setWallet(w.data);
      setInvestments(inv.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const active = investments.filter((i) => i.status === "active");
  const liveTotal = useMemo(
    () => active.reduce((s, i) => s + livePending(i), 0),
    [active, tick] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const collectAll = async () => {
    setCollecting(true);
    try {
      const { data } = await api.post("/investments/collect-all");
      if (data.collected > 0) toast.success(`${azn(data.collected)} cüzdanınıza yığıldı!`);
      else toast.info("Yığılacaq gəlir yoxdur");
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCollecting(false);
    }
  };

  const collectOne = async (id) => {
    try {
      const { data } = await api.post(`/investments/${id}/collect`);
      if (data.collected > 0) toast.success(`${azn(data.collected)} yığıldı!`);
      else toast.info(data.message || "Yığılacaq gəlir yoxdur");
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Salam, {user?.name?.split(" ")[0] || "İnvestor"} 👋</h1>
            <p className="text-muted-foreground mt-1">Portfelinizin ümumi vəziyyəti.</p>
          </div>
          <Link to="/marketplace">
            <Button className="rounded-full" data-testid="dashboard-buy-btn"><Store className="w-4 h-4 mr-2" /> Ağac al</Button>
          </Link>
        </div>

        {/* Live income banner */}
        <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-8 mb-6" data-testid="live-income-banner">
          <div className="noise-overlay absolute inset-0 opacity-10" />
          <div className="relative flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="text-primary-foreground/70 text-sm flex items-center gap-2"><Coins className="w-4 h-4" /> Yığıla bilən gəlir</p>
              <p className="font-heading text-4xl sm:text-5xl font-bold tabular-nums mt-2" data-testid="live-income-value">
                {liveTotal.toLocaleString("az-AZ", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ₼
              </p>
              <p className="text-primary-foreground/60 text-sm mt-1">Gəlir hər saniyə real vaxtda artır</p>
            </div>
            <Button
              onClick={collectAll}
              disabled={collecting || liveTotal <= 0}
              className="rounded-full h-12 px-8 bg-accent text-accent-foreground hover:bg-accent/90 text-base"
              data-testid="collect-all-btn"
            >
              <HandCoins className="w-5 h-5 mr-2" /> Hamısını yığ
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)
          ) : (
            <>
              <StatCard icon={Wallet} label="Balans" value={azn(wallet?.balance)} testid="stat-balance" />
              <StatCard icon={Sprout} label="Aktiv ağaclar" value={wallet?.active_investments ?? 0} testid="stat-active" />
              <StatCard icon={TrendingUp} label="Sərmayə" value={azn(wallet?.total_invested)} accent testid="stat-invested" />
              <StatCard icon={Coins} label="Ümumi qazanc" value={azn(wallet?.total_earned)} accent testid="stat-earned" />
            </>
          )}
        </div>

        <ReferralCard />

        {/* Active investments */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">Aktiv ağaclarım</h2>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-2 gap-4">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
        ) : investments.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center" data-testid="empty-investments">
            <Sprout className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-heading text-lg font-semibold">Hələ ağacınız yoxdur</p>
            <p className="text-muted-foreground mt-1">Bazardan ilk ağacınızı alın və gəlir qazanmağa başlayın.</p>
            <Link to="/marketplace"><Button className="rounded-full mt-5" data-testid="empty-buy-btn">Ağac bazarına keç <ArrowUpRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {investments.map((inv) => {
              const total = new Date(inv.end_at) - new Date(inv.start_at);
              const done = Date.now() - new Date(inv.start_at);
              const pct = Math.min(100, Math.max(0, (done / total) * 100));
              const pending = livePending(inv);
              return (
                <div key={inv.investment_id} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`investment-${inv.investment_id}`}>
                  <div className="flex gap-4 p-5">
                    <img src={inv.tree_image} alt={inv.tree_name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-heading font-semibold truncate">{inv.tree_name}</h3>
                        <Badge variant={inv.status === "active" ? "default" : "secondary"} className="shrink-0">
                          {inv.status === "active" ? `${inv.duration_days} gün` : "Bitib"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">Sərmayə: {azn(inv.principal)} • {azn(inv.daily_income)}/gün</p>
                      <div className="mt-3">
                        <Progress value={pct} className="h-1.5" />
                        <p className="text-xs text-muted-foreground mt-1.5">{pct.toFixed(0)}% tamamlandı</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-secondary/30">
                    <div>
                      <p className="text-xs text-muted-foreground">Yığıla bilər</p>
                      <p className="font-heading font-bold text-primary tabular-nums" data-testid={`pending-${inv.investment_id}`}>
                        {pending.toLocaleString("az-AZ", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ₼
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      disabled={inv.status !== "active" || pending <= 0}
                      onClick={() => collectOne(inv.investment_id)}
                      data-testid={`collect-${inv.investment_id}`}
                    >
                      Yığ
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
